import React, { useRef, useState, useEffect } from 'react';
import { Tabs, Tab, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface TabItem {
    key: string;
    label: string;
    disabled?: boolean;
    hidden?: boolean;
}

interface TabSelectorProps {
    tabs: TabItem[];
    value: number;
    onChange: (index: number) => void;
}

const TabSelector: React.FC<React.PropsWithChildren<TabSelectorProps>> = ({ tabs, value, onChange, children }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [tabMinWidth, setTabMinWidth] = useState(204);
    const { t } = useTranslation();

    const visibleItems = tabs.filter((tab) => !tab.hidden);

    useEffect(() => {
        const calculate = () => {
            if (!containerRef.current) return;
            const containerWidth = containerRef.current.offsetWidth;
            const calculated = Math.floor(containerWidth / tabs.length);
            setTabMinWidth(Math.min(calculated, 204));
        };

        calculate();

        const observer = new ResizeObserver(calculate);
        if (containerRef.current) observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, [tabs.length]);

    const prevValueRef = useRef(value);
    const directionRef = useRef<'ltr' | 'rtl'>('ltr');

    if (value !== prevValueRef.current) {
        directionRef.current = value > prevValueRef.current ? 'ltr' : 'rtl';
        prevValueRef.current = value;
    }
    const direction = directionRef.current;

    return (
        <Box ref={containerRef}>
            <Tabs
                value={value}
                onChange={(_, newValue) => onChange(newValue)}
                className="tab-selector"
                variant="scrollable"
                scrollButtons={false}
                TabIndicatorProps={{
                    style: {
                        backgroundColor: 'var(--main-blue)',
                        height: '1.5px'
                    }
                }}
            >
                {visibleItems.map((item, index) => (
                    <Tab
                        key={index}
                        label={t(item.label)}
                        disableRipple
                        disabled={!!item.disabled}
                        style={{
                            minWidth: tabMinWidth,
                            fontSize: '12px',
                            fontWeight: 400,
                            padding: tabMinWidth < 100 ? '8px 4px' : tabMinWidth < 150 ? '10px 8px' : '12px 16px'
                        }}
                    />
                ))}
            </Tabs>
            <Box className="tab-selector__content">
                <Box key={value} className={`tab-selector__pane tab-selector__pane--${direction}`}>
                    {children}
                </Box>
            </Box>
        </Box>
    );
};

export default TabSelector;
